// Conquest Trail 2.0: in-level GUIDANCE scripts. The trail teaches the game itself, level by level:
// each entry here walks the FIRST play of one campaign level through its new system, using the same
// spotlight runner (src/ui/tutorial.ts) the old chapters used, on the LIVE level - no separate
// classroom world, no resets. Guides run once per profile (storage.campaignGuided), can be skipped,
// and can be disabled outright in Settings ('wi.guidance'). Replays and star runs never see them.
//
// Writing rules (the "one bite" contract):
//   - one new system per level; everything else the level needs was taught before it
//   - every step is ONE sentence of instruction doing ONE real action; no lore dumps inside steps
//   - targets are self-healing closures (recomputed every frame), copied from the chapter idiom:
//     a drifted seed or an odd player order degrades to a console.warn, never a soft-lock

import { unitTargets, unitUnlocked, unitCost, marketCost, barracksCost, harborCost, TECHS } from '../game/engine';
import { BUILDING, NEUTRAL, type GameState, type RegionId, type Unit, type UnitType } from '../game/types';
import type { Step, TutorialApi, TutorialDeps } from './tutorial';

const unit = (type: UnitType, owner: number, hp = 5): Unit => ({ type, owner, movesLeft: 0, hp });
const overviewOpen = (): boolean => !!document.querySelector('.ov-overlay');
const menuOpen = (): boolean => !!document.querySelector('.menu-overlay');
/** The quartermaster's thumb on the scale: top the player's stock up to afford the guided purchase.
 *  Guides run once, on the FIRST play only, and a lesson that cannot be paid for is a broken lesson;
 *  the few goods this grants are noise against a full level's economy. */
const ensureAfford = (g: GameState, cost: readonly number[]): void => {
  const stock = g.factions[g.playerFaction]!.stock;
  for (let i = 0; i < cost.length; i++) if ((stock[i] ?? 0) < cost[i]!) stock[i] = cost[i]!;
};

/** Shared level-guide toolkit (the chapters' `kit` reborn for live campaign worlds). */
function toolkit(deps: TutorialDeps, game: GameState) {
  const P = game.playerFaction;
  const region = (id: RegionId) => game.regions[id]!;
  const cap = game.regions.find((r) => r.owner === P && r.isCapital)!.id;
  const panTile = (id: RegionId): void => {
    const w = deps.regionWorld(id);
    void deps.panTo(w.x, w.y, 320);
  };
  return { P, region, cap, panTile };
}

type GuideFactory = (deps: TutorialDeps, game: GameState, api: TutorialApi) => Step[];

// ================================================================================================
// L1 "The King's Road" - the game's very first touch. Teaches: select, march-to-claim, train at the
// capital, End Turn, and where the mission lives. Combat is NOT taught here (the level's early fights
// are optional skirmishes); reading a fight arrives with First Steel.
// ================================================================================================
const kingsRoad: GuideFactory = (deps, game) => {
  const { P, region, cap, panTile } = toolkit(deps, game);
  const CH = 'The March';
  let N1 = -1;
  let N2 = -1;
  let regionsAtEnter = 0;
  let roundAtEnter = game.round;
  const claimTarget = (from: RegionId): RegionId => {
    const t = unitTargets(game, from);
    return t.move.find((id) => region(id).owner === NEUTRAL) ?? t.move[0] ?? -1;
  };
  return [
    {
      ch: CH,
      text: "🛡 The crown is dead, Warlord, and the king's road belongs to whoever holds it. Your mission sits in the top bar: 🎯 <b>take the raider camp</b>. First, let us get your column marching. Press <b>Next</b>.",
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '⚔️ The star tile is your <b>capital</b>, and your first soldier stands on it. <b>Click the tile</b> to take command; everywhere the soldier can march will light up.',
      target: () => ({ kind: 'tile', id: cap }),
      onEnter: () => {
        deps.selectRegion(null);
        panTile(cap);
      },
      done: () => game.selectedRegion === cap,
    },
    {
      ch: CH,
      text: '🚩 <b>Click the lit tile</b> to march there and <b>claim the land, free</b>. Marching onto land is the only way to own it, and every region you own pays income each round.',
      target: () => (game.selectedRegion === cap ? { kind: 'tile', id: N1 } : { kind: 'tile', id: cap }),
      onEnter: () => {
        deps.selectRegion(cap);
        N1 = claimTarget(cap);
        if (N1 >= 0) panTile(N1);
      },
      done: () => {
        if (N1 < 0) {
          console.warn('[tutorial] l01 guide: no claimable neighbour from the capital; skipping the first claim');
          return true;
        }
        return region(N1).owner === P;
      },
    },
    {
      ch: CH,
      text: '⚒️ One soldier holds no road. <b>Click your capital</b>, then <b>Train a Warrior</b>: troops are raised at your capital, cost is paid up front, and the recruit arrives in a couple of rounds.',
      target: () => (game.selectedRegion === cap ? { kind: 'dom', sel: '.rpanel [data-tut="train-warrior"]' } : { kind: 'tile', id: cap }),
      onEnter: () => {
        deps.selectRegion(null); // the player opens the tile menu themselves: tiles show their options when clicked
        deps.refresh();
        panTile(cap);
      },
      done: () => region(cap).production?.kind === 'unit',
    },
    {
      ch: CH,
      text: '▶ Press <b>End Turn</b>. The raiders move (you will watch it), income lands on your stocks up top, and the road is yours again. When the button <b>glows gold</b>, nothing is left to do.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => {
        deps.selectRegion(null);
        roundAtEnter = game.round;
      },
      done: () => game.round > roundAtEnter,
    },
    {
      ch: CH,
      text: '🏴 That is the whole engine: <b>claim, train, End Turn</b>. <b>Claim one more tile</b> to seal it in.',
      target: () => (game.selectedRegion === cap && N2 >= 0 ? { kind: 'tile', id: N2 } : { kind: 'tile', id: cap }),
      onEnter: () => {
        // the trained Warrior may still be a round out: stand it up now so the closing loop needs no wait
        const c = region(cap);
        if (!c.unit) {
          c.unit = unit(c.production?.kind === 'unit' ? c.production.unitType : 0, P);
          c.production = null;
        }
        c.unit.movesLeft = Math.max(1, c.unit.movesLeft);
        N2 = claimTarget(cap);
        if (N2 < 0) console.warn('[tutorial] l01 guide: no neutral tile left for the second claim; skipping');
        regionsAtEnter = game.regions.filter((r) => r.owner === P).length;
        deps.selectRegion(null);
        deps.refresh();
        panTile(cap);
      },
      done: () => N2 < 0 || game.regions.filter((r) => r.owner === P).length > regionsAtEnter,
    },
    {
      ch: CH,
      text: '🎯 Your mission and your progress live up here; faster victories earn more stars. March on the raider camp, Warlord: hover any enemy before you strike and the forecast will tell you how the fight ends. The road is yours.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// L2 "Ashen Fields" - the economy school. Teaches: the Realm overview, building a Market, income at
// the round tick, and the reclaimable ruins this level is dressed with. Goal (65%) closes it out.
// ================================================================================================
const ashenFields: GuideFactory = (deps, game) => {
  const { P, region, panTile } = toolkit(deps, game);
  const CH = 'The War Chest';
  let M = -1; // the owned tile the Market goes on
  let R = -1; // the nearest reclaimable ruin (a neutral tile with a pre-built Market)
  let roundAtEnter = game.round;
  const marketSpot = (): RegionId => game.regions.find((r) => r.owner === P && !r.buildings.includes(BUILDING.Market) && !r.production)?.id ?? -1;
  return [
    {
      ch: CH,
      text: '🔥 The fields are ash, Warlord, and wars are won by granaries as much as swords. Every region you own pays income each round; buildings multiply it. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🗺️ Open the <b>Realm</b> overview: every tile you own, what it produces, and what sits idle, all on one page.',
      target: () => ({ kind: 'dom', sel: '[data-tut="realm"]' }),
      done: () => overviewOpen(),
    },
    {
      ch: CH,
      text: '🏛️ An idle tile is a wasted tile. <b>Build a Market</b> on the highlighted row: it raises that region\'s output every single round.',
      target: () => (overviewOpen() && M >= 0 ? { kind: 'dom', sel: `.ov-overlay .ov-row[data-rid="${M}"] [data-tut="ov-market"]` } : { kind: 'dom', sel: '[data-tut="realm"]' }),
      onEnter: () => {
        M = marketSpot();
        if (M >= 0) ensureAfford(game, marketCost(game, region(M), P));
        deps.refresh();
      },
      done: () => {
        if (M < 0) {
          console.warn('[tutorial] l02 guide: no idle tile for the Market lesson; skipping');
          return true;
        }
        return game.regions.some((r) => r.owner === P && ((r.production?.kind === 'building' && r.production.building === BUILDING.Market) || r.buildings.includes(BUILDING.Market)));
      },
    },
    {
      ch: CH,
      text: '🏚️ See the grey Market on that tile? A <b>ruin of the burned fields</b>: march a soldier onto it and the whole building is yours, free. Reclaim all three when you can. Press <b>Next</b>.',
      target: () => (R >= 0 ? { kind: 'tile', id: R } : { kind: 'full' }),
      onEnter: () => {
        const myCap = game.regions.find((r) => r.isCapital && r.owner === P);
        R = game.regions
          .filter((r) => r.owner === NEUTRAL && r.buildings.includes(BUILDING.Market))
          .sort((a, b) => (myCap ? Math.abs(a.q - myCap.q) + Math.abs(a.r - myCap.r) - (Math.abs(b.q - myCap.q) + Math.abs(b.r - myCap.r)) : a.id - b.id))[0]?.id ?? -1;
        if (R < 0) console.warn('[tutorial] l02 guide: no ruin market found on this board');
        else panTile(R);
        deps.selectRegion(null);
      },
      manualNext: true,
    },
    {
      ch: CH,
      text: '▶ Press <b>End Turn</b> and watch your stocks up top: the round tick pays every region and every Market at once. Stocks cap at 40, so SPEND: idle gold wins no wars.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => {
        deps.selectRegion(null);
        roundAtEnter = game.round;
      },
      done: () => game.round > roundAtEnter,
    },
    {
      ch: CH,
      text: '🚩 Your goal here is the flag up top: hold <b>65% of the marches</b>. Out-grow the raiders: claim, build, reclaim the ruins, and the fields feed your army all the way there.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// S1 "First Steel" - the combat school. Teaches: inspecting an enemy, the counter wheel (spear beats
// horse), and that every fight is readable before you commit. The scenario stages a worn rider and a
// bloodied warrior on the road; both duels are winnable in one clean strike.
// ================================================================================================
const firstSteel: GuideFactory = (deps, game) => {
  const { P, region, cap, panTile } = toolkit(deps, game);
  const CH = 'First Steel';
  const foeUnit = (type: UnitType): RegionId => game.regions.find((r) => r.unit && r.unit.owner !== P && r.unit.type === type)?.id ?? -1;
  // the staged BRAWLER specifically: the warband's wounded forward warrior, never their fresh capital garrison
  const woundedFoe = (): RegionId => game.regions.find((r) => r.unit && r.unit.owner !== P && r.unit.type === 0 && r.unit.hp <= 2 && !r.isCapital)?.id ?? -1;
  const myUnit = (type: UnitType): RegionId => game.regions.find((r) => r.unit && r.unit.owner === P && r.unit.type === type && r.unit.movesLeft > 0)?.id ?? -1;
  let rider = -1;
  let brawler = -1;
  let roundAtEnter = game.round;
  return [
    {
      ch: CH,
      text: '⚔️ The warband ahead is all horse and iron, Warlord, and every fight in this war is <b>readable before you swing</b>. This road teaches you how. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🐎 <b>Click the horseman</b> to size him up. In any fight, hovering an enemy with your unit selected shows the <b>forecast</b>: the damage both ways, before you commit to anything.',
      target: () => ({ kind: 'tile', id: rider }),
      onEnter: () => {
        rider = foeUnit(2);
        if (rider < 0) console.warn('[tutorial] s01 guide: no enemy rider staged');
        else panTile(rider);
        deps.selectRegion(null);
      },
      done: () => rider < 0 || game.selectedRegion === rider,
    },
    {
      ch: CH,
      text: '🔱 The wheel of war: <b>spears turn horses</b>. Select your Spearman, then <b>strike the rider</b>: the matchup bonus makes this a clean kill.',
      target: () => {
        const spear = myUnit(3);
        return game.selectedRegion === spear && rider >= 0 ? { kind: 'tile', id: rider } : { kind: 'tile', id: spear >= 0 ? spear : cap };
      },
      onEnter: () => {
        deps.selectRegion(null);
        rider = foeUnit(2);
      },
      done: () => rider < 0 || !region(rider).unit || region(rider).unit!.owner === P,
    },
    {
      ch: CH,
      text: '▶ Your spear stands on the fallen horseman, spent for this turn. <b>End Turn</b>: blades rest, and every unit wakes with fresh moves.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => {
        roundAtEnter = game.round;
        deps.selectRegion(null);
      },
      done: () => game.round > roundAtEnter,
    },
    {
      ch: CH,
      text: '🗡️ A bloodied brawler holds the next tile. <b>March your Spearman on and finish him</b>: a wounded blade loses to a fresh one, and the forecast will say exactly that.',
      target: () => {
        const spear = myUnit(3);
        return game.selectedRegion === spear && brawler >= 0 ? { kind: 'tile', id: brawler } : { kind: 'tile', id: spear >= 0 ? spear : cap };
      },
      onEnter: () => {
        brawler = woundedFoe();
        const spear = myUnit(3);
        // the warband may have redeployed overnight (the AI owns its own turns): the LESSON does not
        // negotiate: if the wounded brawler is gone or out of reach, the duel finds the player anyway
        // (a guide-only re-stage beside the spear, the same injection idiom the chapters used)
        if (spear >= 0 && (brawler < 0 || !region(spear).neighbors.includes(brawler))) {
          const foe = game.factions.find((f) => !f.isPlayer && !f.eliminated)?.id ?? -1;
          const spot =
            region(spear).neighbors.map((n) => region(n)).find((r) => !r.unit && r.owner !== P && r.terrain !== 'mountain' && !r.isCapital) ??
            region(spear).neighbors.map((n) => region(n)).find((r) => !r.unit && r.terrain !== 'mountain' && !r.isCapital);
          if (foe >= 0 && spot) {
            spot.owner = foe;
            spot.unit = unit(0, foe, 2);
            brawler = spot.id;
            deps.refresh();
          }
        }
        if (brawler < 0) console.warn('[tutorial] s01 guide: no staged brawler and nowhere to restage; skipping the second duel');
        else panTile(brawler);
        deps.selectRegion(null);
      },
      done: () => brawler < 0 || !region(brawler).unit || region(brawler).unit!.owner === P,
    },
    {
      ch: CH,
      text: '🎯 The road is open and the camp is marked. One law to carry from here: <b>read the forecast, pick the matchup, then swing</b>. Take the warband camp.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// L4 (trail #4) "The Scholar's Pass" - the research school. Teaches: the Research panel, queueing
// Archery, waiting out the scholars, training an Archer, and why arrows matter at walls.
// ================================================================================================
const scholarsPass: GuideFactory = (deps, game) => {
  const { P, region, cap } = toolkit(deps, game);
  const CH = 'The Scholars';
  let roundAtEnter = game.round;
  return [
    {
      ch: CH,
      text: '🛡 The pass fort is sealed, Warlord: walls cannot be climbed and no blade opens that gate. But an arrow does not knock. <b>Your scholars have the answer.</b> Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🔬 A new door has opened on your war table: <b>Research</b>. Open it.',
      target: () => ({ kind: 'dom', sel: '[data-tut="research"]' }),
      done: () => menuOpen(),
    },
    {
      ch: CH,
      text: '🏹 Queue <b>Archery</b>. Research is paid in gold up front and takes a few rounds; cancelling refunds every coin.',
      target: () => (menuOpen() ? { kind: 'dom', sel: '.menu-overlay [data-tut="mil1"]' } : { kind: 'dom', sel: '[data-tut="research"]' }),
      onEnter: () => ensureAfford(game, [TECHS.find((t) => t.id === 'mil1')?.gold ?? 12, 0, 0, 0]),
      done: () => game.factions[P]!.research?.tech === 'mil1' || game.factions[P]!.techs.includes('mil1'),
    },
    {
      ch: CH,
      text: '⏳ The scholars work while the war goes on. <b>End your turns</b>, keep claiming ground, and the moment Archery lands your muster grounds learn a new trade.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => {
        deps.selectRegion(null);
        roundAtEnter = game.round;
      },
      done: () => unitUnlocked(game, P, 1),
    },
    {
      ch: CH,
      text: '🏹 <b>Train an Archer</b> at your capital. Archers loose one free arrow a round without spending their move, and their arrows fly <b>over castle walls</b>: the fort\'s garrison can be worn down from outside.',
      target: () => (game.selectedRegion === cap ? { kind: 'dom', sel: '.rpanel [data-tut="train-archer"]' } : { kind: 'tile', id: cap }),
      onEnter: () => {
        ensureAfford(game, unitCost(game, P, 1));
        deps.selectRegion(null);
        deps.refresh();
      },
      done: () => {
        const pr = region(cap).production;
        return (pr?.kind === 'unit' && pr.unitType === 1) || game.regions.some((r) => r.unit?.owner === P && r.unit.type === 1);
      },
    },
    {
      ch: CH,
      text: '🎯 The law of sieges: <b>gates are the only way in for blades, and arrows do not need a way in</b>. Wear the gate\'s defender down with arrows, then break through. Take the pass fort.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => {
        void roundAtEnter;
        deps.selectRegion(null);
      },
      manualNext: true,
    },
  ];
};

// ================================================================================================
// L5 (trail #5) "Gates of Greyhold" - the barracks school. Teaches: forward muster grounds, the +1
// army cap, the defender's shield, and the reserve queue. Then the two-gate siege closes it out.
// ================================================================================================
const greyhold: GuideFactory = (deps, game) => {
  const { P, region } = toolkit(deps, game);
  const CH = 'The Muster';
  let B = -1; // where the forward Barracks goes: the owned tile nearest the objective keep
  const barracksSpot = (): RegionId => {
    const keep = game.objective ? game.regions[game.objective.region] : undefined;
    const owned = game.regions.filter((r) => r.owner === P && !r.buildings.includes(BUILDING.Barracks) && !r.production);
    if (!owned.length) return -1;
    if (!keep) return owned[0]!.id;
    return owned.sort((a, b) => Math.abs(a.q - keep.q) + Math.abs(a.r - keep.r) - (Math.abs(b.q - keep.q) + Math.abs(b.r - keep.r)) || a.id - b.id)[0]!.id;
  };
  return [
    {
      ch: CH,
      text: '🏰 Greyhold, Warlord: two garrisoned gates, and a long road home. An army that musters at the CAPITAL arrives at the walls exhausted and late. <b>Armies win sieges; muster grounds win armies.</b> Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🗺️ Open the <b>Realm</b> overview and find your ground nearest the castle.',
      target: () => ({ kind: 'dom', sel: '[data-tut="realm"]' }),
      done: () => overviewOpen(),
    },
    {
      ch: CH,
      text: '🛡️ <b>Build a Barracks</b> on the highlighted row: a forward muster ground. It raises your army cap by <b>+1</b>, shields its defender, and every soldier trained there starts the war a march closer.',
      target: () => (overviewOpen() && B >= 0 ? { kind: 'dom', sel: `.ov-overlay .ov-row[data-rid="${B}"] [data-tut="ov-barracks"]` } : { kind: 'dom', sel: '[data-tut="realm"]' }),
      onEnter: () => {
        B = barracksSpot();
        if (B < 0) console.warn('[tutorial] l04 guide: no free owned tile for the Barracks lesson; skipping');
        else ensureAfford(game, barracksCost(game, region(B), P));
        deps.refresh();
      },
      done: () => B < 0 || game.regions.some((r) => r.owner === P && ((r.production?.kind === 'building' && r.production.building === BUILDING.Barracks) || r.buildings.includes(BUILDING.Barracks))),
    },
    {
      ch: CH,
      text: '♟️ One more muster law: a Barracks can train even while a soldier stands on it. The recruit waits as a <b>reserve</b> and steps out a round after the tile clears, so a busy front never stops raising troops. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      manualNext: true,
    },
    {
      ch: CH,
      text: '🎯 Two gates, two plans: split their garrison, or grind one gate with arrows and storm it. Bring Greyhold down.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// L6 (trail #6) "The Last Keep" - the cavalry finale. Archery marched with you (granted by the
// scenario); the lesson is Cavalry: research it, raise a Rider, and feel the two moves a turn.
// ================================================================================================
const lastKeep: GuideFactory = (deps, game) => {
  const { P, region, cap } = toolkit(deps, game);
  const CH = 'The Charge';
  return [
    {
      ch: CH,
      text: '👑 Two lords, two castles, one dying kingdom. Infantry wins gates, Warlord, but only <b>cavalry</b> wins DISTANCE, and this war has two fronts. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🐎 Open <b>Research</b> and queue <b>Cavalry</b>: the last art of the mainland war.',
      target: () => (menuOpen() ? { kind: 'dom', sel: '.menu-overlay [data-tut="mil2"]' } : { kind: 'dom', sel: '[data-tut="research"]' }),
      onEnter: () => ensureAfford(game, [TECHS.find((t) => t.id === 'mil2')?.gold ?? 15, 0, 0, 0]),
      done: () => game.factions[P]!.research?.tech === 'mil2' || game.factions[P]!.techs.includes('mil2'),
    },
    {
      ch: CH,
      text: '⏳ <b>End your turns</b> and press the war meanwhile; the stables open in a few rounds.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => deps.selectRegion(null),
      done: () => unitUnlocked(game, P, 2),
    },
    {
      ch: CH,
      text: '🐎 <b>Train a Rider</b> at your capital. Elite troops also cost your realm\'s ADVANCED resource, the one your home does not grow: conquest and trade feed your stables.',
      target: () => (game.selectedRegion === cap ? { kind: 'dom', sel: '.rpanel [data-tut="train-rider"]' } : { kind: 'tile', id: cap }),
      onEnter: () => {
        ensureAfford(game, unitCost(game, P, 2));
        deps.selectRegion(null);
        deps.refresh();
      },
      done: () => {
        const pr = region(cap).production;
        return (pr?.kind === 'unit' && pr.unitType === 2) || game.regions.some((r) => r.unit?.owner === P && r.unit.type === 2);
      },
    },
    {
      ch: CH,
      text: '🎯 A Rider moves <b>twice</b> a turn: scout with the first step, strike or fall back with the second. Two keeps stand between you and the sea, Warlord. Take them both, and the mainland is yours.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// C1 "First Blood" - the navy school. Teaches: the Harbor, boarding from a port tile, the crossing
// that lands NEXT turn, and that a war across water starts at the shipyard.
// ================================================================================================
const firstBlood: GuideFactory = (deps, game) => {
  const { P, region, cap, panTile } = toolkit(deps, game);
  const CH = 'The Crossing';
  let H = -1; // where the Harbor goes (an owned coastal tile)
  let roundAtEnter = game.round;
  const homeIsland = region(cap).island;
  const harborSpot = (): RegionId => {
    const owned = game.regions.filter((r) => r.owner === P && !r.buildings.includes(BUILDING.Harbor) && !r.production && r.neighbors.length < 6);
    return (owned.find((r) => !r.isCapital) ?? owned[0])?.id ?? -1;
  };
  const myHarbor = (): RegionId => game.regions.find((r) => r.owner === P && r.buildings.includes(BUILDING.Harbor))?.id ?? -1;
  const abroad = (): boolean => game.regions.some((r) => r.owner === P && r.island !== homeIsland);
  return [
    {
      ch: CH,
      text: '⛵ The mainland is ash, Warlord, and the war has taken to the boats. Everything you know still holds; the sea only asks one new thing of you: <b>a Harbor</b>. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🚩 A port needs a SHORE. <b>March your soldier onto the lit coastal tile</b> and claim it: any tile that touches the water will do.',
      target: () => {
        if (harborSpot() >= 0) return { kind: 'tile', id: harborSpot() }; // a shore is already owned: the step passes
        const carrier = game.regions.find((r) => r.unit?.owner === P && r.unit.movesLeft > 0);
        if (!carrier) return { kind: 'dom', sel: '.hud__endturn' }; // moves spent: fresh ones next round
        const shore = unitTargets(game, carrier.id).move.find((id) => region(id).owner === NEUTRAL && region(id).neighbors.length < 6);
        return shore != null ? (game.selectedRegion === carrier.id ? { kind: 'tile', id: shore } : { kind: 'tile', id: carrier.id }) : { kind: 'dom', sel: '.hud__endturn' };
      },
      onEnter: () => deps.selectRegion(null),
      done: () => harborSpot() >= 0,
    },
    {
      ch: CH,
      text: '⚓ <b>Click your shore tile</b>, then <b>build a Harbor</b>: your gateway across the water, and its trade pays +1 Gold every round.',
      target: () => (game.selectedRegion === H && H >= 0 ? { kind: 'dom', sel: '.rpanel [data-tut="build-harbor"]' } : { kind: 'tile', id: H >= 0 ? H : cap }),
      onEnter: () => {
        H = harborSpot();
        if (H < 0) console.warn('[tutorial] c01 guide: no coastal tile for the Harbor lesson');
        else {
          ensureAfford(game, harborCost(game, region(H), P));
          panTile(H);
        }
        deps.selectRegion(null);
        deps.refresh();
      },
      done: () => H < 0 || game.regions.some((r) => r.owner === P && (r.buildings.includes(BUILDING.Harbor) || (r.production?.kind === 'building' && r.production.building === BUILDING.Harbor))),
    },
    {
      ch: CH,
      text: '▶ The shipwrights need a couple of rounds. <b>End your turns</b> and keep claiming your island meanwhile.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => {
        deps.selectRegion(null);
        roundAtEnter = game.round;
      },
      done: () => myHarbor() >= 0,
    },
    {
      ch: CH,
      text: '🚶 A crossing starts ON the port. <b>March a soldier onto your Harbor tile</b>: whoever stands there can board.',
      target: () => {
        const h = myHarbor();
        if (h < 0) return { kind: 'full' };
        if (region(h).unit?.owner === P) return { kind: 'tile', id: h }; // already aboard: the step will pass
        const carrier = game.regions.find((r) => r.unit?.owner === P && r.unit.movesLeft > 0 && unitTargets(game, r.id).move.includes(h));
        return carrier ? (game.selectedRegion === carrier.id ? { kind: 'tile', id: h } : { kind: 'tile', id: carrier.id }) : { kind: 'dom', sel: '.hud__endturn' };
      },
      onEnter: () => {
        void roundAtEnter;
        deps.selectRegion(null);
        const h = myHarbor();
        if (h >= 0) panTile(h);
      },
      done: () => {
        const h = myHarbor();
        return h >= 0 && region(h).unit?.owner === P;
      },
    },
    {
      ch: CH,
      text: '⛵ The far shores are lit. <b>Click a beach across the water</b>: your soldier boards, sails through the night, and lands at the START of your next turn.',
      target: () => {
        const h = myHarbor();
        if (h < 0 || region(h).unit?.owner !== P) return { kind: 'full' };
        if (game.selectedRegion !== h) return { kind: 'tile', id: h };
        const sea = unitTargets(game, h).sea;
        const beach = sea.find((id) => region(id).owner === NEUTRAL && !region(id).unit) ?? sea[0];
        return beach != null ? { kind: 'tile', id: beach } : { kind: 'full' };
      },
      onEnter: () => deps.selectRegion(null),
      done: () => game.voyages.some((v) => v.owner === P) || abroad(),
    },
    {
      ch: CH,
      text: '🌙 The boat is at sea. <b>End Turn</b>: the landing comes with the dawn, and the beach is yours.',
      target: () => ({ kind: 'dom', sel: '.hud__endturn' }),
      onEnter: () => deps.selectRegion(null),
      done: () => abroad(),
    },
    {
      ch: CH,
      text: '🎯 That is naval war entire: <b>Harbor, board, sail, land</b>. Two rivals share this sea; leave neither standing. First blood on the isles, Warlord.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// C2 "Iron Coast" - the census school. Teaches: the Standings panel, reading every realm's bar, and
// that domination is a race you can win without a single duel.
// ================================================================================================
const ironCoast: GuideFactory = (deps) => {
  const CH = 'The Ledger';
  return [
    {
      ch: CH,
      text: '⚒️ The dwarves out-dig you, Warlord, so out-GROW them: this war is won at <b>60% of the conquerable land</b>, and not one duel is owed to anyone. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '📊 <b>Open the Standings</b> (top left): every realm\'s bar races toward the same line. When yours stalls, expand; when a rival\'s surges, worry.',
      target: () => ({ kind: 'dom', sel: '.stats-panel' }),
      onEnter: () => {
        // make the expand a real click even if the panel starts open: collapse it first (the chapters' trick)
        const panel = document.querySelector('.stats-panel');
        if (panel && !panel.classList.contains('is-collapsed')) (panel.querySelector('.stats-panel__head') as HTMLElement | null)?.click();
        deps.refresh();
      },
      done: () => {
        const panel = document.querySelector('.stats-panel');
        return !!panel && !panel.classList.contains('is-collapsed');
      },
    },
    {
      ch: CH,
      text: '🚩 Your own share lives up here, against the goal. Markets swell it without a sword drawn: every idle tile you develop is a tile the dwarves must now out-mine. Press <b>Next</b>.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
    {
      ch: CH,
      text: '🎯 Race the ledger, Warlord: claim, build, and let the census crown you. (And if the dwarves reach for the same line, remember: a war can still shorten THEIR column.)',
      target: () => ({ kind: 'full' }),
      manualNext: true,
    },
  ];
};

// ================================================================================================
// C3 "The Parley" - the diplomacy school. Teaches: the council, answering an envoy's offer, the war
// declaration clock (grace + cooldowns), and the first law of survival: never fight two fronts.
// ================================================================================================
const theParley: GuideFactory = (deps, game) => {
  const { P } = toolkit(deps, game);
  const CH = 'The Council';
  let pendingOffer = -1;
  return [
    {
      ch: CH,
      text: '🕊️ For the first time, Warlord, an envoy waits at YOUR door. Swords win battles; <b>the council wins wars</b>. Press <b>Next</b>.',
      target: () => ({ kind: 'full' }),
      onEnter: () => deps.selectRegion(null),
    },
    {
      ch: CH,
      text: '🤝 A new door on your war table: <b>Diplomacy</b>. Open it.',
      target: () => ({ kind: 'dom', sel: '[data-tut="diplomacy"]' }),
      done: () => menuOpen(),
    },
    {
      ch: CH,
      text: '📜 The envoy offers a trade. <b>Accept it or refuse it</b>: both are answers, and an offer never waits forever. Read what you GIVE and what you GET; a fair trade feeds both realms.',
      target: () => (menuOpen() ? { kind: 'dom', sel: '.menu-overlay .diplo__offer' } : { kind: 'dom', sel: '[data-tut="diplomacy"]' }),
      onEnter: () => {
        // inject the teachable envoy: a fair 2-for-2 from the nearest living rival (the chapters' idiom;
        // the giver's side is topped up so acceptance can always settle)
        if (pendingOffer < 0 && !game.offers.some((o) => o.to === P)) {
          const envoy = game.factions.find((f) => !f.isPlayer && !f.eliminated);
          if (envoy) {
            envoy.stock[0] = Math.max(envoy.stock[0] ?? 0, 2);
            const offerId = game.nextOfferId++;
            game.offers.push({ id: offerId, from: envoy.id, to: P, kind: 'trade', created: game.round, give: { res: 0, amt: 2 }, want: { res: 1, amt: 2 } });
            pendingOffer = offerId;
            deps.offerToasts.show([offerId]);
          }
        }
        deps.refresh();
      },
      done: () => pendingOffer >= 0 && !game.offers.some((o) => o.id === pendingOffer),
    },
    {
      ch: CH,
      text: '⚔️ War has a clock here: a declaration takes a round to mobilise, and peace made cannot be unmade for a few rounds (nor war ended the turn it starts). <b>Declare with intent, never in temper.</b> Press <b>Next</b>.',
      target: () => ({ kind: 'panel' }),
      manualNext: true,
    },
    {
      ch: CH,
      text: '🎯 The first law of the isles: <b>never fight two fronts</b>. Keep one neighbour talking while you take the Greenwood throne from the other, and watch the council as closely as the map.',
      target: () => ({ kind: 'dom', sel: '.hud__dom' }),
      onEnter: () => deps.selectRegion(null),
      manualNext: true,
    },
  ];
};

/** Guides by level id. A level absent here simply plays unguided (the later trail is the exam). */
export const CAMPAIGN_GUIDES: Record<string, GuideFactory> = {
  l01: kingsRoad,
  l02: ashenFields,
  s01: firstSteel,
  l03: scholarsPass,
  l04: greyhold,
  l05: lastKeep,
  c01: firstBlood,
  c02: ironCoast,
  c03: theParley,
};
